import React, { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Shield, Users, Trophy, Flag, ExternalLink, MessageSquare, PlusCircle,
  Settings, Check, X, ThumbsUp, ThumbsDown, Eye, Crown, ArrowLeft,
  Calendar, Coins, Star, Trash2, UserPlus, AlertTriangle, Briefcase, ChevronsUp, Clock, Mail, Swords, Sparkles, Send
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import ClanSettings from "../components/ClanSettings";

const CLAN_SHOP_FRAMES = [
  { id: "02d111604ea5309ce7dcae5d61cafbb28a61ed57.png", name: "Cyber Dragon", price: 150 },
  { id: "12bb5b310693650c6c5263af99f69d3c25fd771e.png", name: "Neon Apex", price: 200 },
  { id: "46461aaea39b18a4a3da2e6d3cf253006f2d6193.png", name: "Inferno Blaze", price: 250 },
  { id: "5v6PzY8.png", name: "Golden Royalty", price: 350 },
];

function formatTimeAgo(dateStr) {
  if (!dateStr) return "—";
  const now = new Date();
  const date = new Date(dateStr);
  const diff = now - date;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Acum";
  if (mins < 60) return `${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} ore`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} zile`;
  const months = Math.floor(days / 30);
  return `${months} luni`;
}

function formatTotalTime(dateStr) {
  if (!dateStr) return "—";
  const now = new Date();
  const joined = new Date(dateStr);
  const diff = now - joined;
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (days < 1) return "< 1 zi";
  if (days === 1) return "1 zi";
  return `${days} zile`;
}

export default function ClanProfile() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("members");
  const [showSettings, setShowSettings] = useState(false);
  const [inviteUsername, setInviteUsername] = useState("");
  const [depositAmount, setDepositAmount] = useState(50);

  const { data: clan, isLoading, error } = useQuery({
    queryKey: ["clan", id],
    queryFn: async () => {
      const res = await api.get(`/clans/${id}`);
      return res.data;
    }
  });

  const { data: applications, refetch: refetchApps } = useQuery({
    queryKey: ["clan_applications", id],
    queryFn: async () => {
      const res = await api.get(`/clans/${id}/applications`);
      return res.data.applications || [];
    },
    enabled: !!user && !!clan,
  });

  const likeMutation = useMutation({
    mutationFn: () => api.post(`/clans/${id}/like`),
    onSuccess: () => queryClient.invalidateQueries(["clan", id]),
    onError: (err) => toast.error(formatApiError(err))
  });

  const dislikeMutation = useMutation({
    mutationFn: () => api.post(`/clans/${id}/dislike`),
    onSuccess: () => queryClient.invalidateQueries(["clan", id]),
    onError: (err) => toast.error(formatApiError(err))
  });

  const kickMutation = useMutation({
    mutationFn: (targetId) => api.delete(`/clans/${id}/members/${targetId}`),
    onSuccess: () => { toast.success("Membrul a fost eliminat"); queryClient.invalidateQueries(["clan", id]); },
    onError: (err) => toast.error(formatApiError(err))
  });

  const roleMutation = useMutation({
    mutationFn: ({ targetId, role }) => api.put(`/clans/${id}/role/${targetId}`, { role }),
    onSuccess: () => { toast.success("Rol schimbat cu succes"); queryClient.invalidateQueries(["clan", id]); },
    onError: (err) => toast.error(formatApiError(err))
  });

  const disbandMutation = useMutation({
    mutationFn: () => api.delete(`/clans/${id}/disband`),
    onSuccess: () => { toast.success("Clanul a fost desființat"); navigate("/clans"); },
    onError: (err) => toast.error(formatApiError(err))
  });

  const resolveAppMutation = useMutation({
    mutationFn: ({ appId, action }) => api.put(`/clans/applications/${appId}`, { action }),
    onSuccess: (_, { action }) => { toast.success(`Cerere ${action === "accept" ? "acceptată" : "respinsă"}`); refetchApps(); queryClient.invalidateQueries(["clan", id]); },
    onError: (err) => toast.error(formatApiError(err))
  });

  const depositMutation = useMutation({
    mutationFn: async (amount) => {
      const res = await api.post(`/clans/${id}/deposit`, { amount });
      return res.data;
    },
    onSuccess: (data) => {
      toast.success(data?.message || "Donație efectuată cu succes!");
      queryClient.invalidateQueries(["clan", id]);
    },
    onError: (err) => toast.error(formatApiError(err) || "Eroare la donare")
  });

  const buyFrameMutation = useMutation({
    mutationFn: async ({ frame_id, price }) => {
      const res = await api.post(`/clans/${id}/buy-frame`, { frame_id, price });
      return res.data;
    },
    onSuccess: (data) => {
      toast.success(data?.message || "Ramă de clan cumpărată!");
      queryClient.invalidateQueries(["clan", id]);
    },
    onError: (err) => toast.error(formatApiError(err) || "Eroare la cumpărare")
  });

  if (isLoading) {
    return <div className="text-center py-20"><div className="animate-spin w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full inline-block"></div></div>;
  }

  if (error) {
    return (
      <div className="max-w-[1500px] mx-auto py-16 text-center bg-[#0b0e15] border border-white/10 p-8 space-y-4">
        <AlertTriangle className="w-12 h-12 text-red-500 mx-auto opacity-60" />
        <h2 className="text-lg font-black font-chakra text-white">Nu s-au putut încărca datele clanului</h2>
        <p className="text-zinc-500 text-xs font-mono">{formatApiError(error)}</p>
        <Link to="/clans" className="tf-btn-primary tf-clip-btn inline-flex items-center gap-2 px-5 py-2 text-xs font-black uppercase tracking-wider mt-4">
          <ArrowLeft className="w-4 h-4" /> Înapoi la Clanuri
        </Link>
      </div>
    );
  }

  if (!clan) {
    return (
      <div className="max-w-[1500px] mx-auto py-16 text-center bg-[#0b0e15] border border-white/10 p-8 space-y-4">
        <Shield className="w-12 h-12 text-zinc-600 mx-auto opacity-40" />
        <h2 className="text-lg font-black font-chakra text-white">Clanul nu a fost găsit</h2>
        <Link to="/clans" className="tf-btn-primary tf-clip-btn inline-flex items-center gap-2 px-5 py-2 text-xs font-black uppercase tracking-wider mt-4">
          <ArrowLeft className="w-4 h-4" /> Înapoi la Clanuri
        </Link>
      </div>
    );
  }

  const xpMax = 1000 * (clan.level || 1);
  const xpPercent = Math.min(100, Math.round(((clan.xp || 0) / xpMax) * 100));
  const clanCoins = clan.idg_coins ?? clan.pz_coins ?? 0;
  const myRole = user ? clan.roster?.find(m => m.user_id === user.id)?.role : null;
  const isLeader = myRole === "leader";
  const isOfficer = myRole === "officer";
  const canManage = isLeader || isOfficer;
  const userLiked = user && (clan.likes || []).includes(user.id);
  const userDisliked = user && (clan.dislikes || []).includes(user.id);
  const isAdmin = user && (user.role === "admin" || user.role === "root");
  const isConfidentialLocked = clan.is_private && !myRole && !isAdmin;

  const tabs = [
    { key: "info", label: "Informație", icon: "ℹ️" },
    { key: "members", label: "Participanți", icon: "👥" },
    { key: "activity", label: "Activitate", icon: "📊" },
    ...(isConfidentialLocked ? [] : [
      { key: "coins", label: "IDG Coins", icon: "🪙" },
      { key: "announcements", label: "Anunțuri", icon: "📢" },
    ])
  ];

  if (showSettings && canManage) {
    return (
      <div className="max-w-[1200px] mx-auto space-y-6">
        <button onClick={() => setShowSettings(false)} className="inline-flex items-center gap-2 text-zinc-400 hover:text-white bg-[#0b0e15] border border-white/10 px-4 py-2 text-xs font-chakra font-bold uppercase tracking-wider">
          <ArrowLeft className="w-4 h-4" /> Înapoi la Clan
        </button>
        <div className="bg-[#0b0e15] border border-white/10 p-6">
          <ClanSettings clan={clan} onClose={() => setShowSettings(false)} />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1500px] mx-auto space-y-6">
      {/* 1. Top Navigation Button */}
      <div className="flex items-center justify-between">
        <Link 
          to="/clans" 
          className="inline-flex items-center gap-2 text-zinc-300 hover:text-white bg-[#0b0e15] hover:bg-[#121622] px-4 py-2 border border-white/10 transition-colors text-xs font-chakra font-black uppercase tracking-wider"
        >
          <ArrowLeft className="w-4 h-4 text-[var(--tf-primary)]" />
          <span>TOATE CLANURILE</span>
        </Link>
      </div>

      {/* 2. Hero Clan Banner & Identity */}
      <div className="bg-[#0b0e15] border border-white/10 overflow-hidden relative shadow-2xl">
        {/* Banner Image */}
        <div className="relative h-[250px] md:h-[300px] w-full bg-zinc-950 overflow-hidden">
          <img 
            src={clan.banner_url || "https://images.unsplash.com/photo-1542751371-adc38448a05e?auto=format&fit=crop&w=800"} 
            alt={clan.name} 
            className="w-full h-full object-cover opacity-75" 
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0b0e15] via-[#0b0e15]/40 to-transparent" />
          
          {/* Top Right Floating Telemetry on Banner */}
          <div className="absolute top-4 right-4 flex items-center bg-black/70 backdrop-blur-md px-3.5 py-1.5 gap-4 border border-white/10 font-mono text-xs shadow-lg">
            <button
              onClick={() => user && likeMutation.mutate()}
              disabled={!user}
              className={`flex items-center gap-1.5 font-bold transition-colors ${userLiked ? 'text-emerald-400' : 'text-zinc-400 hover:text-white'}`}
              title="Apreciază clanul"
            >
              <ThumbsUp className="w-3.5 h-3.5" /> {(clan.likes || []).length}
            </button>
            <button
              onClick={() => user && dislikeMutation.mutate()}
              disabled={!user}
              className={`flex items-center gap-1.5 font-bold transition-colors ${userDisliked ? 'text-red-400' : 'text-zinc-400 hover:text-white'}`}
              title="Nu-mi place"
            >
              <ThumbsDown className="w-3.5 h-3.5" /> {(clan.dislikes || []).length}
            </button>
            <div className="flex items-center gap-1.5 text-zinc-400 pl-2 border-l border-white/10">
              <Eye className="w-3.5 h-3.5" /> {clan.views || 0}
            </div>
          </div>
        </div>

        {/* Logo and Identity */}
        <div className="relative -mt-16 sm:-mt-20 px-6 pb-6 flex flex-col items-center">
          <div className="relative w-32 h-32 sm:w-40 sm:h-40 flex items-center justify-center z-10">
            {/* Base Logo Image */}
            <div className="w-24 h-24 sm:w-32 sm:h-32 overflow-hidden rounded-xl bg-black/40 flex items-center justify-center shadow-2xl">
              <img src={clan.logo_url} alt={clan.name} className="w-full h-full object-cover" />
            </div>

            {/* Avatar Frame (No Border, pure frame asset) */}
            {clan.avatar_frame && (
              <img 
                src={`${process.env.PUBLIC_URL || ''}/frames/${clan.avatar_frame}`} 
                alt="Avatar Frame" 
                className="absolute inset-0 w-full h-full object-contain pointer-events-none drop-shadow-[0_0_20px_rgba(0,0,0,0.9)]" 
              />
            )}
          </div>

          <div className="mt-4 text-center">
            <div className="flex items-center justify-center gap-2.5 flex-wrap">
              <h1 className="text-2xl sm:text-4xl font-black text-white font-chakra uppercase tracking-wider drop-shadow-lg">
                {clan.name}
              </h1>
              <span 
                className="text-xs font-black font-chakra px-2 py-0.5 text-black uppercase shadow-md" 
                style={{ backgroundColor: clan.accent_color || 'var(--tf-primary)' }}
              >
                {clan.tag}
              </span>
              {clan.is_private && (
                <span className="text-xs font-black font-chakra px-2 py-0.5 bg-amber-500/15 text-amber-400 border border-amber-500/30 uppercase flex items-center gap-1">
                  🔒 Privat
                </span>
              )}
            </div>

            {clan.motto && (
              <p className="text-xs text-[var(--tf-primary)] font-mono italic mt-1.5 font-bold">
                "{clan.motto}"
              </p>
            )}

            <p className="text-xs text-zinc-400 font-mono mt-2 max-w-2xl mx-auto">
              {clan.description || "O echipă de legendă pe serverele INDUNGI."}
            </p>
          </div>
        </div>
      </div>

      {/* 3. Five Telemetry Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="bg-[#0b0e15] border border-white/10 p-4 flex flex-col items-center justify-center text-center">
          <Trophy className="w-5 h-5 text-amber-400 mb-1.5" />
          <div className="text-[10px] text-zinc-500 uppercase font-chakra font-bold tracking-widest">Poziție</div>
          <div className="text-lg font-black text-white font-chakra mt-0.5">#{clan.rank || "1"}</div>
        </div>

        <div className="bg-[#0b0e15] border border-white/10 p-4 flex flex-col items-center justify-center text-center">
          <Users className="w-5 h-5 text-cyan-400 mb-1.5" />
          <div className="text-[10px] text-zinc-500 uppercase font-chakra font-bold tracking-widest">Participanți</div>
          <div className="text-lg font-black text-white font-chakra mt-0.5">{clan.member_count || 0}/{clan.max_members || 100}</div>
        </div>

        <div className="bg-[#0b0e15] border border-white/10 p-4 flex flex-col items-center justify-center text-center">
          <Coins className="w-5 h-5 text-emerald-400 mb-1.5" />
          <div className="text-[10px] text-zinc-500 uppercase font-chakra font-bold tracking-widest">Banc IDG</div>
          <div className="text-lg font-black text-white font-chakra mt-0.5">
            {isConfidentialLocked ? (
              <span className="text-xs font-mono text-zinc-500">🔒 Confidențial</span>
            ) : (
              `${clanCoins.toLocaleString('ro-RO')} IDG`
            )}
          </div>
        </div>

        <div className="bg-[#0b0e15] border border-white/10 p-4 flex flex-col items-center justify-center text-center">
          <Star className="w-5 h-5 text-[#5e72e4] mb-1.5" />
          <div className="text-[10px] text-zinc-500 uppercase font-chakra font-bold tracking-widest">Evaluare</div>
          <div className="text-lg font-black text-white font-chakra mt-0.5">{(clan.rating || 0).toLocaleString('ro-RO')}</div>
        </div>

        <div className="bg-[#0b0e15] border border-white/10 p-4 flex flex-col items-center justify-center text-center col-span-2 md:col-span-1">
          <ChevronsUp className="w-5 h-5 mb-1.5" style={{ color: clan.accent_color || 'var(--tf-primary)' }} />
          <div className="text-[10px] text-zinc-500 uppercase font-chakra font-bold tracking-widest">Nivel {clan.level || 1}</div>
          <div className="w-full h-1.5 bg-black mt-1.5 overflow-hidden border border-white/5">
            <div className="h-full" style={{ width: `${xpPercent}%`, backgroundColor: clan.accent_color || 'var(--tf-primary)' }} />
          </div>
          <div className="text-[9px] font-mono text-zinc-500 mt-1">{clan.xp || 0}/{xpMax} XP</div>
        </div>
      </div>

      {/* 4. Action Buttons Row */}
      <div className="flex flex-wrap items-center justify-center gap-3">
        {canManage && (
          <>
            <button 
              onClick={() => setShowSettings(true)} 
              className="bg-[#0b0e15] hover:bg-[#121622] border border-white/10 hover:border-white/30 text-white px-5 py-2.5 text-xs font-chakra font-bold transition-colors flex items-center gap-2 uppercase tracking-wider"
            >
              <Settings className="w-4 h-4 text-amber-400" /> 
              <span>Editează</span>
            </button>

            <button 
              onClick={() => setActiveTab("applications")} 
              className="bg-[#0b0e15] hover:bg-[#121622] border border-white/10 hover:border-white/30 text-white px-5 py-2.5 text-xs font-chakra font-bold transition-colors flex items-center gap-2 uppercase tracking-wider relative"
            >
              <Flag className="w-4 h-4 text-cyan-400" /> 
              <span>Cereri</span>
              {applications?.length > 0 && (
                <span className="px-1.5 py-0.2 text-[9px] font-black text-black font-chakra" style={{ backgroundColor: clan.accent_color || 'var(--tf-primary)' }}>
                  {applications.length}
                </span>
              )}
            </button>

            {isLeader && (
              <button
                onClick={() => { if (window.confirm("Ești sigur că vrei să desființezi clanul?")) disbandMutation.mutate(); }}
                className="bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-400 px-5 py-2.5 text-xs font-chakra font-bold transition-colors flex items-center gap-2 uppercase tracking-wider"
              >
                <Trash2 className="w-4 h-4" /> 
                <span>Desființează</span>
              </button>
            )}
          </>
        )}

        {user && !myRole && (
          <button
            onClick={() => api.post(`/clans/${id}/apply`).then(() => toast.success("Cerere trimisă!")).catch(e => toast.error(formatApiError(e)))}
            className="tf-btn-primary tf-clip-btn px-8 py-2.5 text-xs font-black uppercase tracking-widest flex items-center gap-2 shadow-lg"
          >
            <Mail className="w-4 h-4" /> 
            <span>APLICĂ ACUM ÎN CLAN</span>
          </button>
        )}
      </div>

      {/* 5. Main Tabs Bar & Body */}
      <div className="bg-[#0b0e15] border border-white/10">
        <div className="flex border-b border-white/10 gap-1 px-4 pt-3 overflow-x-auto scrollbar-hide">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2.5 text-xs font-chakra font-black uppercase tracking-wider transition-all border-b-2 whitespace-nowrap ${
                activeTab === tab.key
                  ? "border-[var(--tf-primary)] text-white bg-white/5"
                  : "border-transparent text-zinc-500 hover:text-zinc-300 hover:bg-white/5"
              }`}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        <div className="p-6">
          {/* ===== TAB: INFO ===== */}
          {activeTab === "info" && (
            <div className="space-y-6">
              <div>
                <h3 className="text-sm font-black font-chakra text-white mb-2 uppercase flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-[var(--tf-primary)]" /> 
                  <span>Despre Clan</span>
                </h3>
                <div className="text-zinc-300 font-mono text-xs leading-relaxed bg-[#07090e] p-4 border border-white/10">
                  {clan.description || "Nu există o descriere configurată pentru acest clan."}
                </div>
              </div>

              {(clan.social_links?.discord || clan.social_links?.steam || clan.social_links?.website) && (
                <div>
                  <h3 className="text-xs font-black font-chakra text-white mb-2 uppercase">Canale Sociale & Conexiuni</h3>
                  <div className="flex flex-wrap gap-2">
                    {clan.social_links?.discord && (
                      <a href={clan.social_links.discord} target="_blank" rel="noreferrer" className="bg-[#5865F2]/15 text-[#5865F2] hover:bg-[#5865F2]/25 px-3 py-2 border border-[#5865F2]/30 text-xs font-mono font-bold flex items-center gap-2">
                        <MessageSquare className="w-4 h-4" /> Discord
                      </a>
                    )}
                    {clan.social_links?.steam && (
                      <a href={clan.social_links.steam} target="_blank" rel="noreferrer" className="bg-white/5 text-white hover:bg-white/10 px-3 py-2 border border-white/10 text-xs font-mono font-bold flex items-center gap-2">
                        <ExternalLink className="w-4 h-4" /> Steam Community
                      </a>
                    )}
                    {clan.social_links?.website && (
                      <a href={clan.social_links.website} target="_blank" rel="noreferrer" className="bg-white/5 text-white hover:bg-white/10 px-3 py-2 border border-white/10 text-xs font-mono font-bold flex items-center gap-2">
                        <ExternalLink className="w-4 h-4" /> Website
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ===== TAB: MEMBERS ===== */}
          {activeTab === "members" && (
            <div className="space-y-4">
              <h3 className="text-sm font-black font-chakra text-white uppercase flex items-center gap-2">
                <Users className="w-4 h-4 text-[var(--tf-primary)]" /> 
                <span>Membri activi ai clanului</span>
              </h3>

              {/* Invite Bar */}
              {canManage && (
                <div className="flex gap-2 bg-[#07090e] border border-white/10 p-2.5">
                  <input
                    type="text"
                    placeholder="Introdu username-ul membrului de invitat..."
                    className="flex-1 bg-black/50 border border-white/10 px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-[var(--tf-primary)]"
                    value={inviteUsername}
                    onChange={e => setInviteUsername(e.target.value)}
                  />
                  <button className="tf-btn-primary tf-clip-btn px-4 py-2 text-xs font-black uppercase tracking-wider flex items-center gap-1.5">
                    <UserPlus className="w-3.5 h-3.5" /> 
                    <span>INVITĂ</span>
                  </button>
                </div>
              )}

              {/* Members Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-left font-mono text-xs">
                  <thead>
                    <tr className="border-b border-white/10 text-[10px] text-zinc-500 font-chakra uppercase tracking-wider font-bold">
                      <th className="pb-3 px-3">#</th>
                      <th className="pb-3 px-3">Membru</th>
                      <th className="pb-3 px-3">Rank Clan</th>
                      <th className="pb-3 px-3">Rol Forum</th>
                      <th className="pb-3 px-3">Postări</th>
                      <th className="pb-3 px-3">Topicuri</th>
                      <th className="pb-3 px-3">Timp Total</th>
                      <th className="pb-3 px-3">Ultima Activitate</th>
                      {canManage && <th className="pb-3 px-3">Acțiuni</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {(!clan.roster || clan.roster.length === 0) ? (
                      <tr>
                        <td colSpan={canManage ? 9 : 8} className="text-center py-8 text-zinc-500 font-mono text-xs">
                          Nu există membri înregistrați în clan.
                        </td>
                      </tr>
                    ) : (
                      clan.roster.map((member, i) => (
                        <tr key={member.user_id} className="hover:bg-[#0e131d] transition-colors">
                          <td className="py-3 px-3 font-chakra font-bold">
                            {member.role === "leader" ? <Crown className="w-4 h-4 text-amber-400" /> : member.role === "officer" ? <Shield className="w-4 h-4 text-sky-400" /> : <span>{i + 1}</span>}
                          </td>
                          <td className="py-3 px-3">
                            <Link to={`/u/${member.username}`} className="flex items-center gap-2.5 group">
                              <img src={member.avatarUrl || `https://ui-avatars.com/api/?background=random&name=${member.username}`} alt="" className="w-7 h-7 border border-white/10 group-hover:border-[var(--tf-primary)] transition-colors" />
                              <span className="text-white font-bold font-chakra group-hover:text-[var(--tf-primary)] transition-colors">{member.username}</span>
                            </Link>
                          </td>
                          <td className="py-3 px-3">
                            {member.role === "leader" && <span className="bg-amber-500/20 text-amber-400 border border-amber-500/30 px-2 py-0.5 text-[9px] font-black uppercase font-chakra">Lider</span>}
                            {member.role === "officer" && <span className="bg-sky-500/20 text-sky-400 border border-sky-500/30 px-2 py-0.5 text-[9px] font-black uppercase font-chakra">Co-Lider</span>}
                            {member.role === "member" && <span className="bg-white/5 text-zinc-400 border border-white/10 px-2 py-0.5 text-[9px] font-bold uppercase font-chakra">Membru</span>}
                          </td>
                          <td className="py-3 px-3">
                            <span className="text-[10px] font-bold px-2 py-0.5 border" style={{ color: member.group_color, borderColor: `${member.group_color}40`, backgroundColor: `${member.group_color}15` }}>
                              {member.group_name}
                            </span>
                          </td>
                          <td className="py-3 px-3 text-zinc-300">{member.message_count || 0}</td>
                          <td className="py-3 px-3 text-zinc-300">{member.topic_count || 0}</td>
                          <td className="py-3 px-3 text-zinc-400">{formatTotalTime(member.joined_at)}</td>
                          <td className="py-3 px-3 text-zinc-400">{formatTimeAgo(member.last_seen)}</td>
                          {canManage && (
                            <td className="py-3 px-3">
                              {member.user_id !== user?.id && member.role !== "leader" && (
                                <div className="flex items-center gap-1.5">
                                  {isLeader && (
                                    <>
                                      <select
                                        className="bg-black/50 border border-white/10 text-zinc-300 text-[10px] font-bold px-1.5 py-1 focus:outline-none"
                                        value={member.role}
                                        onChange={e => roleMutation.mutate({ targetId: member.user_id, role: e.target.value })}
                                      >
                                        <option value="member">Membru</option>
                                        <option value="officer">Co-Lider</option>
                                      </select>
                                      <button
                                        onClick={() => { if (window.confirm(`Transfer leadership la ${member.username}?`)) roleMutation.mutate({ targetId: member.user_id, role: "leader" }); }}
                                        className="bg-amber-500/20 text-amber-400 border border-amber-500/30 px-2 py-1 text-[10px] font-bold hover:bg-amber-500/30 transition-colors"
                                      >
                                        LIDER
                                      </button>
                                    </>
                                  )}
                                  <button
                                    onClick={() => { if (window.confirm(`Elimini pe ${member.username} din clan?`)) kickMutation.mutate(member.user_id); }}
                                    className="bg-red-500/20 text-red-400 border border-red-500/30 px-2 py-1 text-[10px] font-bold hover:bg-red-500/30 transition-colors"
                                  >
                                    KICK
                                  </button>
                                </div>
                              )}
                              {member.user_id === user?.id && member.role === "leader" && <span className="text-zinc-600 text-xs">—</span>}
                            </td>
                          )}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ===== TAB: APPLICATIONS ===== */}
          {activeTab === "applications" && canManage && (
            <div className="space-y-4">
              <h3 className="text-sm font-black font-chakra text-white uppercase flex items-center gap-2">
                <Flag className="w-4 h-4 text-cyan-400" /> 
                <span>Cereri de alăturare în așteptare</span>
              </h3>
              {!applications || applications.length === 0 ? (
                <div className="text-center py-12 text-zinc-500 font-mono text-xs bg-[#07090e] border border-white/5">
                  <Flag className="w-8 h-8 mx-auto mb-2 opacity-30 text-cyan-400" />
                  <p>Nu există cereri de alăturare în așteptare.</p>
                </div>
              ) : (
                <div className="space-y-2.5">
                  {applications.map(app => (
                    <div key={app.id} className="bg-[#07090e] border border-white/10 p-3.5 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <img src={app.avatarUrl || `https://ui-avatars.com/api/?background=random&name=${app.username}`} className="w-9 h-9 border border-white/10" alt="" />
                        <div>
                          <Link to={`/u/${app.username}`} className="text-white font-bold font-chakra text-xs hover:text-[var(--tf-primary)]">{app.username}</Link>
                          <div className="text-[10px] font-mono text-zinc-500">Aplicat {new Date(app.createdAt).toLocaleDateString("ro-RO")}</div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => resolveAppMutation.mutate({ appId: app.id, action: "accept" })} className="bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 p-2 transition-colors border border-emerald-500/30"><Check className="w-4 h-4" /></button>
                        <button onClick={() => resolveAppMutation.mutate({ appId: app.id, action: "reject" })} className="bg-red-500/20 hover:bg-red-500/30 text-red-400 p-2 transition-colors border border-red-500/30"><X className="w-4 h-4" /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ===== TAB: ACTIVITY ===== */}
          {activeTab === "activity" && (
            <div className="text-center py-12 text-zinc-500 font-mono text-xs bg-[#07090e] border border-white/5">
              <Swords className="w-8 h-8 mx-auto mb-2 opacity-30 text-[var(--tf-primary)]" />
              <p>Nu există meciuri sau activități recente înregistrate pentru acest clan.</p>
            </div>
          )}

          {/* ===== TAB: IDG COINS & VAULT ===== */}
          {activeTab === "coins" && (
            <div className="space-y-6">
              {/* Vault Overview Card */}
              <div className="bg-[#07090e] border border-amber-500/30 p-6 flex flex-col md:flex-row items-center justify-between gap-6 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-full bg-gradient-to-l from-amber-500/10 via-transparent to-transparent pointer-events-none" />
                
                <div className="flex items-center gap-4 relative z-10">
                  <div className="w-16 h-16 bg-amber-500/20 flex items-center justify-center border-2 border-amber-500/50 shadow-[0_0_20px_rgba(245,158,11,0.2)]">
                    <span className="text-amber-400 font-black font-chakra text-2xl">IDG</span>
                  </div>
                  <div>
                    <span className="text-[10px] font-chakra font-bold text-zinc-400 uppercase tracking-widest block">Seiful & Fondul de Aur al Clanului</span>
                    <div className="text-3xl font-black font-chakra text-white mt-0.5">
                      {clanCoins.toLocaleString('ro-RO')} <span className="text-amber-400 text-lg">IDG COINS</span>
                    </div>
                    <p className="text-xs text-zinc-400 font-mono mt-1">
                      💡 Fondurile de IDG Coins sunt folosite pentru achiziția de rame exclusive și avansarea nivelului de clan.
                    </p>
                  </div>
                </div>

                {user && (
                  <div className="bg-black/60 border border-white/10 p-3.5 text-center min-w-[180px] relative z-10">
                    <span className="text-[10px] text-zinc-500 uppercase font-chakra font-bold block">Punctele tale personale</span>
                    <span className="font-chakra font-black text-amber-400 text-lg">{user.points || 0} IDG</span>
                  </div>
                )}
              </div>

              {/* Deposit Section (Active for all clan members or users) */}
              {user && (
                <div className="bg-[#07090e] border border-white/10 p-5 space-y-4">
                  <div className="flex items-center justify-between border-b border-white/10 pb-3">
                    <h4 className="text-xs font-black font-chakra text-white uppercase flex items-center gap-2">
                      <Send className="w-4 h-4 text-emerald-400" />
                      <span>Donează IDG Coins în Seiful Echipei</span>
                    </h4>
                    <span className="text-[10px] font-mono text-emerald-400 bg-emerald-500/10 px-2 py-0.5 border border-emerald-500/30">
                      +1 Clan XP la fiecare 2 IDG Coins donați
                    </span>
                  </div>

                  <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-1.5">
                      {[25, 50, 100, 250, 500].map(amt => (
                        <button
                          key={amt}
                          type="button"
                          onClick={() => setDepositAmount(amt)}
                          className={`px-3 py-1.5 text-xs font-mono font-bold transition-all border ${
                            depositAmount === amt 
                              ? 'bg-amber-500 text-black border-amber-500 shadow-md' 
                              : 'bg-white/5 border-white/10 text-zinc-300 hover:text-white hover:bg-white/10'
                          }`}
                        >
                          +{amt} IDG
                        </button>
                      ))}
                    </div>

                    <div className="flex-1 min-w-[140px] flex items-center gap-2">
                      <input 
                        type="number"
                        min="1"
                        max={user.points || 10000}
                        value={depositAmount}
                        onChange={e => setDepositAmount(Math.max(1, parseInt(e.target.value) || 1))}
                        className="w-full bg-black/60 border border-white/10 px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-amber-500"
                        placeholder="Sumă..."
                      />
                      <button
                        type="button"
                        disabled={depositMutation.isPending || (user.points || 0) < depositAmount}
                        onClick={() => depositMutation.mutate(depositAmount)}
                        className="tf-btn-primary tf-clip-btn px-5 py-2 text-xs font-black uppercase tracking-wider whitespace-nowrap disabled:opacity-30"
                      >
                        {depositMutation.isPending ? "Se transferă..." : "TRANSFERĂ ÎN SEIF"}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Clan Frame Store */}
              <div className="space-y-3">
                <h4 className="text-xs font-black font-chakra text-white uppercase flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-amber-400" />
                  <span>Magazin Rame Exclusive de Clan</span>
                </h4>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
                  {CLAN_SHOP_FRAMES.map((cf) => {
                    const isPurchased = (clan.purchased_frames || []).includes(cf.id);
                    const isEquipped = clan.avatar_frame === cf.id;
                    return (
                      <div key={cf.id} className="bg-[#07090e] border border-white/10 p-3.5 flex flex-col justify-between items-center text-center space-y-3">
                        <div className="relative w-20 h-20 flex items-center justify-center">
                          <img src={clan.logo_url} alt="" className="w-12 h-12 object-cover rounded-lg" />
                          <img src={`${process.env.PUBLIC_URL || ''}/frames/${cf.id}`} alt={cf.name} className="absolute inset-0 w-full h-full object-contain pointer-events-none drop-shadow-md" />
                        </div>

                        <div>
                          <span className="font-chakra font-bold text-xs text-white block">{cf.name}</span>
                          <span className="font-mono text-[11px] text-amber-400 font-bold">{cf.price} IDG Coins</span>
                        </div>

                        {canManage && (
                          <div className="w-full">
                            {isPurchased ? (
                              <span className="px-2.5 py-1 text-[10px] font-chakra font-bold uppercase bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 block">
                                {isEquipped ? "ECHIPATĂ" : "DEȚINUTĂ"}
                              </span>
                            ) : (
                              <button
                                type="button"
                                disabled={buyFrameMutation.isPending || clanCoins < cf.price}
                                onClick={() => buyFrameMutation.mutate({ frame_id: cf.id, price: cf.price })}
                                className="w-full py-1.5 text-[10px] font-chakra font-black uppercase tracking-wider bg-amber-500 hover:bg-amber-400 text-black transition-all disabled:opacity-30"
                              >
                                Cumpără pentru Clan
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* ===== TAB: ANNOUNCEMENTS ===== */}
          {activeTab === "announcements" && (
            <div className="text-center py-12 text-zinc-500 font-mono text-xs bg-[#07090e] border border-white/5">
              <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-30 text-[var(--tf-primary)]" />
              <p>Niciun anunț postat de conducerea clanului.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
